import { Currency } from "../../models/financial/currency.js";
import { validateAndFormatAddress } from "../../utils/validateAndParseAddress.js";
import { getVirtualEnvironment } from "../../utils/utils.js";
import invariant from "tiny-invariant";
export class FungibleToken extends Currency {
    static toFungibleToken(token) {
        if (!token) {
            return undefined;
        }
        const { address, platformId, chainId, currency, coinGeckoId, isNative } = token;
        return new FungibleToken(BigInt(platformId ?? chainId), address, currency.decimals, currency.asset.symbol, currency.asset.name, currency?.logoUrl, undefined, isNative, coinGeckoId);
    }
    /**
     * @param platformId The platform ID on which this token resides
     * @param address The contract address on the chain on which this token lives
     * @param decimals {@link Currency#decimals}
     * @param symbol {@link Currency#symbol}
     * @param name {@link Currency#name}
     * @param bypassChecksum If true it only checks for length === 42, startsWith 0x and contains only hex characters
     */
    constructor(platformId, address, decimals, symbol, name, logoUrl, bypassChecksum, isNative, coinGeckoId, isStablecoin) {
        super(decimals, symbol, name, logoUrl, isNative, true, coinGeckoId, isStablecoin);
        this.isToken = true;
        const vmType = getVirtualEnvironment(platformId);
        if (bypassChecksum) {
            const formattedAddress = validateAndFormatAddress(address, vmType);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        else if (!isNative) {
            const formattedAddress = validateAndFormatAddress(address, vmType);
            invariant(formattedAddress, `${address} is not a valid address.`);
            this.address = formattedAddress;
        }
        else {
            this.address = address;
        }
        this.isNative = isNative || false;
        this.platformId = platformId;
    }
    /**
     * Returns true if the two tokens are equivalent, i.e. have the same platformId and address.
     * @param other other token to compare
     */
    equals(other) {
        if (this === other)
            return true;
        return (this.platformId == other?.platformId &&
            validateAndFormatAddress(this.address, getVirtualEnvironment(this.platformId)) ==
                validateAndFormatAddress(other?.address, getVirtualEnvironment(other?.platformId)));
    }
    /**
     * Returns the currency object that this token represents
     */
    currency() {
        return this;
    }
    toFungibleTokenModel() {
        return {
            __typename: "FungibleToken",
            currency: this.toCurrencyModel(),
            address: validateAndFormatAddress(this.address, getVirtualEnvironment(this.platformId)),
            chainId: this.platformId,
            platformId: this.platformId,
            isNative: this.isNative,
            coinGeckoId: this.coinGeckoId,
        };
    }
    identifier() {
        return FungibleToken.identifier(this.platformId, this.address);
    }
    static identifier(platformId, address) {
        const vmType = getVirtualEnvironment(platformId);
        return `${platformId.toString()}:${validateAndFormatAddress(address, vmType)}`;
    }
}
